import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { CondominiumService } from '@core/services';
import {
  ApiResponse,
  Condominium,
  CondominiumFull,
  CondominiumDetail,
  CondominiumCreate,
  CondominiumDashboard,
  CondominiumUpdate,
  MaintenanceFeeUpdate,
  Unit
} from '@core/types';
import { QUERY_KEYS } from '@core/constans';
import { lastValueFrom } from 'rxjs';
import { profileStore } from '@core/store';
import { environment } from '@environments/environment';

/**
 * Query service para operaciones de Condominium
 * Utiliza TanStack Query para caché inteligente, mutations y sincronización automática
 */
@Injectable({ providedIn: 'root' })
export class CondominiumQuery {
  private condominiumService = inject(CondominiumService);
  private queryClient = inject(QueryClient);
  private profileStore = inject(profileStore);

  /**
   * Query para obtener lista de condominios (switch)
   * Usado para el selector de condominio activo
   * Incluye tenantId en el queryKey para invalidar cache cuando cambie el tenant
   */
  useCondominiumSwitch() {
    return injectQuery(() => {
      const tenantId = this.profileStore.data.activeTenantId();
      return {
        queryKey: [...QUERY_KEYS.condominiums.switch(), tenantId],
        queryFn: () => lastValueFrom(this.condominiumService.switch()),
        select: (data: ApiResponse<Condominium[]>) => data.data || [],
        staleTime: environment.staleTime,
        enabled: !!tenantId, // Solo ejecutar si hay tenantId
      };
    });
  }

  /**
   * Query para obtener detalles de un condominio específico
   * @param id - ID del condominio
   */
  useCondominiumDetail(id: string) {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.condominiums.detail(id),
      queryFn: () => lastValueFrom(this.condominiumService.get(id)),
      select: (data: ApiResponse<CondominiumFull>) => data.data,
      enabled: !!id, // Solo ejecutar si hay ID
    }));
  }

  /**
   * Query reactivo para obtener detalles del condominio activo
   * Evita estados "pending" infinitos cuando el id se setea después.
   */
  useActiveCondominiumDetail() {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      return {
        queryKey: QUERY_KEYS.condominiums.detail(condominiumId || ''),
        queryFn: () => lastValueFrom(this.condominiumService.get(condominiumId || '')),
        select: (data: ApiResponse<CondominiumFull>) => data.data,
        enabled: !!condominiumId,
      };
    });
  }

  /**
   * Query para obtener detalles completos de un condominio (incluye zonas y unidades)
   * @param id - ID del condominio
   */
  useCondominiumFullDetail(id: string) {
    return injectQuery(() => ({
      queryKey: [...QUERY_KEYS.condominiums.detail(id), 'full'],
      queryFn: () => lastValueFrom(this.condominiumService.getDetail(id)),
      select: (data: ApiResponse<CondominiumDetail>) => data.data,
      enabled: !!id,
    }));
  }

  /**
   * Mutation para crear un nuevo condominio
   * Invalida automáticamente la lista de condominios al completarse
   */
  useCreateCondominium() {
    return injectMutation(() => ({
      mutationFn: (data: CondominiumCreate) =>
        lastValueFrom(this.condominiumService.create(data)),
      onSuccess: () => {
        // Invalidar todas las queries de condominios para refetch automático
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.all
        });
      },
    }));
  }

  /**
   * Mutation para actualizar un condominio existente
   * Invalida el condominio específico y la lista general
   */
  useUpdateCondominium() {
    return injectMutation(() => ({
      mutationFn: (data: CondominiumFull) =>
        lastValueFrom(this.condominiumService.update(data)),
      onSuccess: (_result, variables) => {
        // Invalidar el condominio específico
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.detail(variables.id)
        });
        // Invalidar también la lista
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.switch()
        });
      },
    }));
  }

  /**
   * Mutation para eliminar un condominio
   * Invalida todas las queries de condominios
   */
  useDeleteCondominium() {
    return injectMutation(() => ({
      mutationFn: (id: string) =>
        lastValueFrom(this.condominiumService.delete(id)),
      onSuccess: () => {
        // Invalidar todas las queries de condominios
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.all
        });
      },
    }));
  }

  /**
   * Mutation para actualizar una unidad
   * Invalida el detalle completo del condominio asociado
   */
  useUpdateUnit() {
    return injectMutation(() => ({
      mutationFn: (unit: Unit) =>
        lastValueFrom(this.condominiumService.updateUnit(unit)),
      onSuccess: (_result, variables) => {
        // Invalidar el detalle completo para refrescar las unidades
        // Nota: Necesitamos el ID del condominio, que debería estar en el contexto
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.details()
        });
      },
    }));
  }

  /**
   * Query para obtener el dashboard del condominio activo
   * Incluye condominiumId en el queryKey para invalidar cache cuando cambie el condominio
   */
  useDashboard() {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      return {
        queryKey: [...QUERY_KEYS.condominiums.dashboard(), condominiumId],
        queryFn: () => lastValueFrom(this.condominiumService.getDashboard()),
        select: (data: ApiResponse<CondominiumDashboard>) => data.data,
        staleTime: environment.staleTime,
        enabled: !!condominiumId, // Solo ejecutar si hay condominiumId
      };
    });
  }

  /**
   * Mutation para actualizar condominio completo
   * Invalida el condominio específico, la lista y el dashboard
   */
  useUpdateCondominiumFull() {
    return injectMutation(() => ({
      mutationFn: (data: CondominiumUpdate) =>
        lastValueFrom(this.condominiumService.updateCondominium(data)),
      onSuccess: (_result, variables) => {
        // Invalidar el condominio específico
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.detail(variables.id)
        });
        // Invalidar la lista
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.switch()
        });
        // Invalidar el dashboard
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.dashboard()
        });
      },
    }));
  }

  /**
   * Mutation para actualizar cuota de mantenimiento masivamente
   * Invalida todas las queries relacionadas con unidades y dashboard
   */
  useUpdateMaintenanceFeeBulk() {
    return injectMutation(() => ({
      mutationFn: (data: MaintenanceFeeUpdate) =>
        lastValueFrom(this.condominiumService.updateMaintenanceFeeBulk(data)),
      onSuccess: () => {
        // Invalidar detalles de condominios (incluye unidades)
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.details()
        });
        // Invalidar el dashboard
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.dashboard()
        });
        // Invalidar las queries de unidades si existen
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units?.all || ['units']
        });
      },
    }));
  }
}

